import { Link } from 'react-router';
import { useState, useEffect, useRef } from 'react';
import { usePrizePool, formatPrizeUsd } from '../hooks/usePrizePool';
import { useSeasonStore } from '../stores/seasonStore';
import LazyVideo from '../components/ui/LazyVideo';

// Phase display config
const PHASE_LABELS: Record<string, { title: string; next: string }> = {
  QUALIFIER: { title: 'Qualifiers End', next: 'Shortlist begins after' },
  SHORTLIST: { title: 'Shortlist Ends', next: 'Semifinals begin after' },
  SEMIFINAL: { title: 'Semifinals End', next: 'Finals begin after' },
  FINAL: { title: 'Finals End', next: 'Season concludes' },
  CLOSED: { title: 'Season Ended', next: '' },
};

export default function Landing() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const circleRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const { prizePoolUsd } = usePrizePool();

  // Season store for dynamic phase data
  const {
    phaseType,
    phaseName,
    phaseEndTime,
    totalPlayers,
    qualificationLine,
    fetchSeasonStatus,
  } = useSeasonStore();

  // Fetch season status on mount
  useEffect(() => {
    fetchSeasonStatus();
    // Refresh every 5 minutes
    const interval = setInterval(fetchSeasonStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSeasonStatus]);

  // Format prize for display - show actual or placeholder
  const prizeDisplay = prizePoolUsd ? formatPrizeUsd(prizePoolUsd) : '$—';

  // Phase display info
  const phaseLabel = PHASE_LABELS[phaseType] ?? PHASE_LABELS.QUALIFIER ?? { title: 'Phase Ends', next: '' };

  // Track mouse for interactive circle
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (circleRef.current) {
        const rect = circleRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const deltaX = (e.clientX - centerX) * 0.015;
        const deltaY = (e.clientY - centerY) * 0.015;
        setMousePos({ x: deltaX, y: deltaY });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Countdown timer - calculate from phaseEndTime
  useEffect(() => {
    const updateCountdown = () => {
      if (!phaseEndTime) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const endMs = new Date(phaseEndTime).getTime();
      const remaining = Math.max(0, endMs - Date.now());

      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [phaseEndTime]);

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background: 'radial-gradient(ellipse 80% 50% at 50% 50%, rgba(255,255,255,0.03) 0%, transparent 60%)',
              transform: `translateY(${scrollY * 0.3}px)`,
            }}
          />
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }} />
        </div>

        {/* Top bar - Brand announcement */}
        <div className="relative z-10 border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff88] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff88]"></span>
            </span>
            <span className="text-xs tracking-[0.25em] uppercase text-white/60">
              Season 1 Qualifiers Now Live — <span className="text-white">{prizeDisplay} Prize Pool</span>
              <span className="inline-flex items-center gap-1 ml-2 text-[#22c55e]/80">
                <svg className="w-2.5 h-2.5 animate-bounce" style={{ animationDuration: '2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <span className="text-[10px]">Growing</span>
              </span>
            </span>
          </div>
        </div>

        {/* Main hero content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 pt-8 pb-20">
          {/* Beat Games Brand */}
          <div className="mb-6 animate-slide-up-fade">
            <div className="flex items-center gap-4">
              <img
                src="/images/bg-logo.svg"
                alt="Beat Games"
                className="h-16 md:h-20 w-auto"
              />
              <div className="h-12 w-px bg-white/10 hidden sm:block" />
              <div className="hidden sm:block">
                <div className="text-[10px] tracking-[0.5em] text-white/30 mb-1">ESPORTS</div>
                <div className="font-display text-2xl md:text-3xl tracking-[0.3em] font-light">
                  BEAT<span className="text-white/40">GAMES</span>
                </div>
              </div>
            </div>
          </div>

          {/* Presents line */}
          <div
            className="mb-8 animate-slide-up-fade"
            style={{ animationDelay: '0.1s' }}
          >
            <span className="text-[10px] tracking-[0.5em] uppercase text-white/30">Presents</span>
          </div>

          {/* Hold Steady - Game Title */}
          <div
            className="relative mb-8 animate-slide-up-fade"
            style={{ animationDelay: '0.15s' }}
          >
            {/* Decorative lines */}
            <div className="absolute -left-20 top-1/2 w-16 h-px bg-gradient-to-r from-transparent to-white/20" />
            <div className="absolute -right-20 top-1/2 w-16 h-px bg-gradient-to-l from-transparent to-white/20" />

            <h1 className="font-display text-[clamp(3.5rem,12vw,9rem)] leading-[0.9] tracking-[0.04em] text-center">
              <span className="block">HOLD</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-b from-white to-white/40">STEADY</span>
            </h1>
          </div>

          {/* Interactive Circle Preview */}
          <div
            ref={circleRef}
            className="relative mb-10 animate-slide-up-fade"
            style={{ animationDelay: '0.2s' }}
          >
            {/* Outer glow */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[180px] h-[180px] rounded-full bg-white/5 blur-3xl" />
            </div>

            {/* Rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[160px] h-[160px] rounded-full border border-white/[0.08] animate-pulse" style={{ animationDuration: '3s' }} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[120px] h-[120px] rounded-full border border-white/[0.12]" />
            </div>

            {/* Main circle */}
            <div
              className="relative w-[80px] h-[80px] rounded-full border-2 border-white/80 flex items-center justify-center transition-transform duration-100"
              style={{ transform: `translate(${mousePos.x}px, ${mousePos.y}px)` }}
            >
              {/* Crosshair */}
              <div className="absolute w-3 h-px bg-white/60" />
              <div className="absolute w-px h-3 bg-white/60" />
              <div className="w-1.5 h-1.5 rounded-full bg-white" />
            </div>
          </div>

          {/* Tagline */}
          <p
            className="text-base md:text-lg text-white/40 text-center max-w-md mb-10 animate-slide-up-fade tracking-wide"
            style={{ animationDelay: '0.25s' }}
          >
            Keep your cursor inside the shrinking circle.<br />
            <span className="text-white/70">45 seconds. {prizeDisplay}. One chance.</span>
          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row items-center gap-4 animate-slide-up-fade"
            style={{ animationDelay: '0.3s' }}
          >
            <Link
              to="/play"
              className="group relative px-10 py-4 bg-white text-black font-bold text-sm tracking-[0.15em] uppercase overflow-hidden transition-all duration-300 hover:shadow-[0_0_50px_rgba(255,255,255,0.25)]"
            >
              <span className="relative z-10">Play Now</span>
            </Link>
            <Link
              to="/rules"
              className="px-10 py-4 border border-white/20 text-white/60 font-medium text-sm tracking-[0.15em] uppercase hover:border-white/40 hover:text-white transition-all duration-300"
            >
              How It Works
            </Link>
          </div>
        </div>

        {/* Bottom stats bar */}
        <div className="relative z-10 border-t border-white/[0.06] bg-black/40 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-5">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 md:gap-8">
              {[
                { value: prizeDisplay, label: 'Prize Pool', isGrowing: true },
                { value: '45s', label: 'Duration' },
                { value: qualificationLine.toLocaleString(), label: phaseName.replace('Open ', '') },
                { value: totalPlayers.toLocaleString(), label: 'Players', hideOnMobile: true },
                { value: 'LIVE', label: 'Season 1', isLive: true, hideOnMobile: true },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`text-center ${stat.hideOnMobile ? 'hidden md:block' : ''}`}
                >
                  <div className={`font-display text-xl md:text-2xl mb-1 ${stat.isLive ? 'text-[#00ff88]' : 'text-white'}`}>
                    {stat.value}
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-white/30">{stat.label}</span>
                    {stat.isGrowing && (
                      <svg className="w-2.5 h-2.5 text-[#22c55e]/60 animate-bounce" style={{ animationDuration: '2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Game Section */}
      <section className="relative py-32 px-4 border-t border-white/[0.06]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#050505] to-[#030303]" />

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-20">
            <p className="text-[10px] tracking-[0.4em] uppercase text-white/30 mb-4">The Challenge</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-wide">THE GAME</h2>
          </div>

          {/* Game explanation */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Video */}
            <div className="relative flex items-center justify-center">
              {/* Glow effect behind video */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[80%] h-[80%] bg-white/[0.02] blur-3xl rounded-full" />
              </div>

              {/* Video container */}
              <LazyVideo
                src="/BeatGamesLaunch.mp4"
                className="relative w-full max-w-lg aspect-video rounded-lg border border-white/10 bg-[#0a0a0a]"
                autoPlay
                loop
              />
            </div>

            {/* Right - Description */}
            <div>
              <div className="space-y-8">
                {[
                  {
                    num: '01',
                    title: 'The Circle Shrinks',
                    desc: 'Starting at 200px, the circle shrinks to just 20px over 45 seconds. It also moves and jitters unpredictably.'
                  },
                  {
                    num: '02',
                    title: 'Stay Inside',
                    desc: 'Keep your cursor within the boundary. Leave for more than 100ms and you\'re eliminated instantly.'
                  },
                  {
                    num: '03',
                    title: 'Survive & Qualify',
                    desc: 'Complete the full 45 seconds to post your score. Top 256 players qualify for the live finals.'
                  },
                ].map((step) => (
                  <div key={step.num} className="flex gap-6">
                    <div className="font-mono text-sm text-white/20">{step.num}</div>
                    <div>
                      <h3 className="font-display text-xl mb-2 tracking-wide">{step.title}</h3>
                      <p className="text-white/40 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prize Section */}
      <section className="relative py-32 px-4 border-t border-white/[0.06]">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Prize amount */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <p className="text-[10px] tracking-[0.4em] uppercase text-white/30">Season 1 Prize Pool</p>
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/20">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22c55e]"></span>
                  </span>
                  <span className="text-[9px] tracking-[0.1em] uppercase text-[#22c55e]/80 font-medium">Growing</span>
                </div>
              </div>
              <div className="font-display text-[clamp(4rem,10vw,8rem)] leading-none mb-6 tracking-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffd700] via-[#ffec80] to-[#ffd700]">
                  {prizeDisplay}
                </span>
              </div>
              <p className="text-white/40 text-lg leading-relaxed max-w-md">
                Sixteen finalists compete in a live broadcast event.
                One winner takes home <span className="text-white">50%</span> of the prize pool.
              </p>
              <p className="text-white/25 text-sm mt-3">
                Pool grows with every game played until finals conclude.
              </p>
            </div>

            {/* Right - Breakdown */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { place: '1st', amount: prizePoolUsd ? formatPrizeUsd(prizePoolUsd * 0.5) : '50%', color: 'from-[#ffd700] to-[#b8860b]', bg: 'bg-[#ffd700]/5' },
                { place: '2nd', amount: prizePoolUsd ? formatPrizeUsd(prizePoolUsd * 0.25) : '25%', color: 'from-[#c0c0c0] to-[#808080]', bg: 'bg-white/5' },
                { place: '3rd', amount: prizePoolUsd ? formatPrizeUsd(prizePoolUsd * 0.15) : '15%', color: 'from-[#cd7f32] to-[#8b4513]', bg: 'bg-[#cd7f32]/5' },
                { place: '4th', amount: prizePoolUsd ? formatPrizeUsd(prizePoolUsd * 0.1) : '10%', color: 'from-white/60 to-white/30', bg: 'bg-white/[0.02]' },
              ].map((prize) => (
                <div
                  key={prize.place}
                  className={`${prize.bg} border border-white/[0.06] p-6 transition-all duration-300 hover:border-white/20`}
                >
                  <div className={`font-display text-3xl mb-1 text-transparent bg-clip-text bg-gradient-to-r ${prize.color}`}>
                    {prize.place}
                  </div>
                  <div className="font-mono text-lg text-white/70">{prize.amount}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Section */}
      <section className="relative py-32 px-4 border-t border-white/[0.06] bg-[#020202]">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Phase indicator */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff88] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff88]"></span>
            </span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#00ff88]/80">{phaseName}</span>
          </div>
          <p className="text-[10px] tracking-[0.4em] uppercase text-white/30 mb-4">{phaseLabel.title}</p>

          <div className="flex justify-center gap-3 md:gap-6 mb-12">
            {[
              { value: countdown.days, label: 'Days' },
              { value: countdown.hours, label: 'Hours' },
              { value: countdown.minutes, label: 'Mins' },
              { value: countdown.seconds, label: 'Secs' },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-white/[0.03] border border-white/[0.06] px-4 py-6 md:px-8 md:py-8 min-w-[70px] md:min-w-[100px]"
              >
                <div className="font-display text-3xl md:text-5xl tabular-nums">
                  {item.value.toString().padStart(2, '0')}
                </div>
                <div className="text-white/20 text-[10px] tracking-[0.2em] uppercase mt-2">{item.label}</div>
              </div>
            ))}
          </div>

          <p className="text-white/30 mb-8">
            <span className="text-[#00ff88] font-semibold">{totalPlayers.toLocaleString()}</span> players competing for <span className="text-white">{qualificationLine.toLocaleString()}</span> {phaseType === 'QUALIFIER' ? 'qualification' : phaseType === 'FINAL' ? 'winner' : 'advancement'} spots
          </p>

          <Link
            to="/leaderboard"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm tracking-[0.15em] uppercase transition-colors"
          >
            View Leaderboard
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-4 border-t border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-radial from-white/[0.02] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img
              src="/images/bg-logo.svg"
              alt="Beat Games"
              className="h-10 w-auto opacity-50"
            />
            <span className="font-display text-lg tracking-[0.25em] text-white/30">BEATGAMES</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl mb-6 tracking-wide">
            PROVE YOUR<br />PRECISION
          </h2>
          <p className="text-white/40 text-lg mb-12 max-w-lg mx-auto">
            The circle is waiting. How long can you hold steady?
          </p>
          <Link
            to="/play"
            className="inline-block px-14 py-5 bg-white text-black font-bold text-sm tracking-[0.2em] uppercase hover:shadow-[0_0_60px_rgba(255,255,255,0.3)] transition-all duration-300"
          >
            Enter The Arena
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/[0.06] py-12 px-4 bg-[#020202]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <img src="/images/bg-logo.svg" alt="Beat Games" className="h-8 w-auto opacity-60" />
              <span className="font-display text-sm tracking-[0.25em] text-white/40">BEATGAMES</span>
            </div>

            <div className="flex items-center gap-8 text-xs text-white/30">
              <Link to="/rules" className="hover:text-white transition-colors tracking-[0.15em] uppercase">Rules</Link>
              <Link to="/leaderboard" className="hover:text-white transition-colors tracking-[0.15em] uppercase">Leaderboard</Link>
              <Link to="/transparency" className="hover:text-white transition-colors tracking-[0.15em] uppercase">Transparency</Link>
              <Link to="/finals" className="hover:text-white transition-colors tracking-[0.15em] uppercase">Archive</Link>
              <a href="https://x.com/beatgames_" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="https://t.me/beatgamesfun" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                </svg>
              </a>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/[0.04] text-center text-white/15 text-[10px] tracking-[0.2em] uppercase">
            © 2026 Beat Games. All Rights Reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
